import * as Sentry from '@sentry/vue';

// Each wallet panel creates its own Recurly/Braintree strategy, so one Braintree outage
// makes every panel report the same cause at once. Sentry's Dedupe keys on message +
// stacktrace (which differ per panel), so it misses these — collapse them here instead.
// The panels still set their own methodError, so nothing user-facing is suppressed.
const DEDUPE_WINDOW_MS = 10_000;
const recentSignatures = new Map(); // signature -> last-seen epoch ms

const isEcho = (signature) => {
  const now = Date.now();
  for (const [sig, seenAt] of recentSignatures) {
    if (now - seenAt > DEDUPE_WINDOW_MS) recentSignatures.delete(sig);
  }
  const echo = recentSignatures.has(signature);
  recentSignatures.set(signature, now);
  return echo;
};

// Recurly.js and Braintree emit plain error-like objects, not Error instances.
// Wrap them so Sentry shows a readable title and groups by operation + code
// instead of "Object captured as exception with keys: ...".
export const capturePaymentError = (operation, err, extra = {}) => {
  const code = err?.code || err?.cause?.code || err?.statusCode || err?.name || 'unknown';

  // Sentry's @password scrubber nulls any value containing "token"/"auth", which wipes
  // extra.recurlyError for these errors. Tag values survive it, so expose detail as tags.
  const cause = err?.cause;
  const kind = cause?.type ?? err?.type; // MERCHANT | NETWORK | CUSTOMER | ...
  const gateway = cause?.name ?? err?.name; // e.g. BraintreeError
  const gatewayCode = cause?.code; // underlying gateway code, if any
  const causeMessage = cause?.message;

  // Recurly tags both mount-time setup (`-api-error`) and purchase-time tokenization
  // (`-tokenize-`) as `<wallet>_tokenize`; split them so init failures aren't mistaken for it.
  const phase = /tokenize/i.test(code) ? 'tokenize'
    : /api-error/i.test(code) ? 'init'
      : undefined;

  // Key on the gateway cause (wallet-agnostic) so one outage collapses across panels;
  // fall back to operation+code otherwise so unrelated errors are never merged.
  const signature = gatewayCode ? `${kind || '-'}:${gatewayCode}` : `${operation}:${code}`;
  if (isEcho(signature)) return;

  Sentry.captureException(new Error(`${operation} ${code}: ${err?.message}`), {
    tags: {
      operation,
      recurlyErrorCode: code,
      ...(kind && { paymentErrorKind: kind }),
      ...(gateway && { gateway }),
      ...(gatewayCode && { gatewayCode }),
      ...(phase && { phase }),
    },
    contexts: {
      paymentError: { code, kind, gateway, gatewayCode, causeMessage, phase },
    },
    extra: { recurlyError: err, ...extra },
  });
};
